import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "../button";
import ConfirmDialog from "../ConfirmDialog";
import { underV3 } from "../v3/flagged";
export function NoteGenerationNotice({
  error,
  recovery,
  loading,
  submitting = false,
  quietWhileRunning = false,
  onRetry,
  onAbandon,
}: {
  error: string | null;
  recovery: boolean;
  loading: boolean;
  submitting?: boolean;
  quietWhileRunning?: boolean;
  onRetry: () => Promise<void>;
  onAbandon: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [showSeparateConfirmation, setShowSeparateConfirmation] =
    useState(false);
  // While this screen's own request runs, its submission is saved as
  // pending and reads as recovery: the progress shows instead. A request
  // left by another screen still shows the (disabled) recovery choices,
  // unless `quietWhileRunning`: the visit page, whose Create → New note
  // sheet shows that request's own progress (staging check 04/11).
  const showRecovery =
    recovery && !submitting && !(quietWhileRunning && loading);
  if (!error && !showRecovery) return null;
  return (
    <div
      role="alert"
      className={cn("space-y-3", underV3("font-v3 text-[14px] leading-5"))}
    >
      {error && (
        <p
          className={
            underV3("rounded-xl bg-v3-rec-tint px-4 py-3 text-v3-rec-text") ||
            undefined
          }
        >
          {error}
        </p>
      )}
      {showRecovery && (
        <>
          <p className={underV3("text-v3-text-2") || undefined}>
            {t("visit.recovery.noteDescription")}
          </p>
          <Button
            disabled={loading}
            onClick={() => {
              void onRetry();
            }}
          >
            {t("visit.recovery.retrySaved")}
          </Button>
          <ConfirmDialog
            open={showSeparateConfirmation}
            onOpenChange={setShowSeparateConfirmation}
            title={t("visit.recovery.separateTitle")}
            description={t("visit.recovery.separateDescription")}
            confirmText={t("visit.recovery.separateConfirm")}
            trigger={
              <Button variant="outline" disabled={loading}>
                {t("visit.recovery.separateConfirm")}
              </Button>
            }
            onConfirm={() => {
              if (!loading) onAbandon();
            }}
          />
        </>
      )}
    </div>
  );
}
